import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, Eye, ArrowRight, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  ia: 'Inteligencia Artificial',
  neuro: 'Neuromarketing',
  marketing: 'Marketing',
  branding: 'Branding',
  negocio: 'Negocios'
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const url = activeCategory === 'all' ? `${API}/blog` : `${API}/blog?categoria=${activeCategory}`;
    fetch(url).then(r => r.json()).then(d => setPosts(d.posts || [])).catch(console.error);
  }, [activeCategory]);

  const filteredPosts = posts.filter(p =>
    searchTerm === '' || p.titulo.toLowerCase().includes(searchTerm.toLowerCase()) || p.extracto.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background pt-24 pb-golden-2xl">
      <section className="py-golden-xl">
        <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg">
          <div className="text-center mb-golden-xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-golden-lg">
              <BookOpen className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">Conocimiento que transforma</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-golden-md">
              Blog de <span>Neuralia<span className="bg-[linear-gradient(90deg,#4285F4_0%,#EA4335_33%,#FBBC05_66%,#34A853_100%)] bg-clip-text text-transparent">369</span></span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="text-lg text-text-body max-w-2xl mx-auto mb-golden-lg">
              Artículos, guías y análisis sobre IA, neuromarketing y estrategia empresarial.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="max-w-md mx-auto relative mb-golden-lg">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <input type="text" placeholder="Buscar artículos..." value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="input-kinesthetic pl-12" data-testid="blog-search" />
            </motion.div>
            <div className="flex flex-wrap justify-center gap-3">
              <button onClick={() => setActiveCategory('all')}
                className={`px-4 py-2 rounded-full transition-all text-sm ${activeCategory === 'all' ? 'bg-primary text-background' : 'bg-card hover:bg-card-hover text-text-body border border-border-subtle'}`}
                data-testid="blog-filter-all">Todos</button>
              {Object.entries(CATEGORY_LABELS).map(([id, label]) => (
                <button key={id} onClick={() => setActiveCategory(id)}
                  className={`px-4 py-2 rounded-full transition-all text-sm ${activeCategory === id ? 'bg-primary text-background' : 'bg-card hover:bg-card-hover text-text-body border border-border-subtle'}`}
                  data-testid={`blog-filter-${id}`}>{label}</button>
              ))}
            </div>
          </div>

          {filteredPosts.length === 0 ? (
            <div className="text-center py-golden-2xl">
              <p className="text-text-muted text-lg">No se encontraron artículos.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-golden-lg">
              {filteredPosts.map((post, index) => (
                <motion.div key={post.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: index * 0.05 }}>
                  <Link to={`/blog/${post.slug}`} className="glass-card p-golden-lg block group hover:border-primary/30 transition-all h-full"
                    data-testid={`blog-card-${post.slug}`}>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-golden-md ${
                      post.categoria === 'ia' ? 'bg-primary/10 text-primary' :
                      post.categoria === 'neuro' ? 'bg-purple-500/10 text-purple-400' :
                      post.categoria === 'marketing' ? 'bg-secondary/10 text-secondary' :
                      post.categoria === 'branding' ? 'bg-accent/10 text-accent' :
                      'bg-green-500/10 text-green-400'
                    }`}>
                      {CATEGORY_LABELS[post.categoria] || post.categoria}
                    </span>
                    <h3 className="text-lg mb-golden-sm group-hover:text-primary transition-colors">{post.titulo}</h3>
                    <p className="text-text-body text-sm mb-golden-md line-clamp-3">{post.extracto}</p>
                    <div className="flex items-center justify-between text-xs text-text-muted mt-auto">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.fecha?.split('T')[0]}</span>
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{post.vistas}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
